import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Siren,
  Users,
  MessageSquareText,
  Radio,
  Wrench,
  LifeBuoy,
  ClipboardCheck,
  ShieldAlert,
  ArrowRightLeft,
  Brain,
  Clock,
  Shield,
  Award,
  BookOpen,
  Workflow,
} from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface CodeRougeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabKey = 'esprit' | 'equipe' | 'roles' | 'raci' | 'postmortem';

function Pill({ children, tone }: { children: React.ReactNode; tone: 'red' | 'amber' | 'slate' | 'emerald' }) {
  const map: Record<typeof tone, string> = {
    red: 'bg-red-500/15 text-red-200 border-red-400/20',
    amber: 'bg-amber-500/15 text-amber-200 border-amber-400/20',
    slate: 'bg-white/8 text-white/80 border-white/10',
    emerald: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/20',
  };
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${map[tone]}`}>{children}</span>;
}

function BadgeRA({ value }: { value: 'R' | 'A' | 'C' | 'I' | '—' }) {
  const cls =
    value === 'A'
      ? 'bg-red-500/20 text-red-100 border-red-400/25'
      : value === 'R'
        ? 'bg-amber-500/20 text-amber-100 border-amber-400/25'
        : value === 'C'
          ? 'bg-sky-500/20 text-sky-100 border-sky-400/25'
          : value === 'I'
            ? 'bg-slate-500/25 text-white/80 border-white/10'
            : 'bg-white/5 text-white/60 border-white/10';

  return (
    <span className={`inline-flex w-9 justify-center rounded-lg border px-2 py-1 text-xs font-extrabold ${cls}`} title={value}>
      {value}
    </span>
  );
}

export default function CodeRougeModal({ isOpen, onClose }: CodeRougeModalProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('esprit');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['code_rouge_last_ai_analysis'] ?? null) : null;

  const tabs = useMemo(
    () => [
      { id: 'esprit' as const, label: 'Esprit', icon: Siren },
      { id: 'equipe' as const, label: 'Équipe & War Room', icon: Users },
      { id: 'roles' as const, label: 'Rôles & responsabilités', icon: ClipboardCheck },
      { id: 'raci' as const, label: 'RACI holistique', icon: ArrowRightLeft },
      { id: 'postmortem' as const, label: 'Postmortem & amélioration', icon: ShieldAlert },
    ],
    [],
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-linear-to-br from-slate-950 via-slate-900 to-black rounded-2xl w-[92vw] h-[92vh] border border-white/10 shadow-2xl shadow-black/50 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="p-5 md:p-6 border-b border-white/10 bg-linear-to-r from-red-600/25 via-rose-600/15 to-indigo-600/15">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-red-500/20 border border-red-400/20 flex items-center justify-center shadow-inner">
                      <Siren className="w-6 h-6 text-red-200" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">CODE ROUGE BZ</h2>
                        <Pill tone="red">CLOUD</Pill>
                        <Pill tone="amber">CRISE</Pill>
                        <Pill tone="slate">FRONT UNI</Pill>
                        {/* Badge AI - Disponible dans AI Assistant */}
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                          className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                          title="Disponible dans AI Assistant"
                        >
                          <Brain className="w-4 h-4 text-purple-200" />
                          <span className="text-xs text-purple-100 font-semibold">AI</span>
                        </motion.div>
                      </div>
                      <p className="text-sm text-white/75 mt-1">
                        Quand on active le mode <strong>CODE ROUGE</strong>, BZ se mobilise cross-départements pour stabiliser, communiquer et
                        restaurer — vite, propre, et avec une gouvernance claire.
                      </p>
                      {lastAnalysisDate && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-white/70">
                          <Clock className="w-3.5 h-3.5" />
                          <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={onClose}
                    className="p-2 rounded-xl hover:bg-white/10 transition-colors text-white/80 hover:text-white"
                    aria-label="Fermer"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
                  {tabs.map((t) => {
                    const Icon = t.icon;
                    const isActive = activeTab === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setActiveTab(t.id)}
                        className={`inline-flex items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold border transition-colors ${
                          isActive
                            ? 'bg-white text-slate-950 border-white'
                            : 'bg-white/5 text-white/85 border-white/10 hover:bg-white/8'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-5 md:p-8">
                <AnimatePresence mode="wait">
                  {activeTab === 'esprit' && (
                    <motion.div
                      key="esprit"
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      transition={{ duration: 0.22 }}
                      className="space-y-6"
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 lg:col-span-2">
                          <h3 className="text-lg font-bold text-white mb-2">L’esprit CODE ROUGE</h3>
                          <p className="text-sm text-white/80 leading-relaxed">
                            CODE ROUGE, c’est notre mode <strong>urgence structurée</strong>. On n’improvise pas, on exécute un cadre. Le but:
                            réduire l’impact client, restaurer la stabilité, et protéger la confiance (interne + externe).
                          </p>
                          <ul className="mt-4 space-y-2 text-sm text-white/80">
                            <li className="flex items-start gap-2">
                              <span className="text-red-200 mt-0.5">•</span>
                              <span>
                                <strong>Un front uni</strong>: communication + coordination + technique → même vérité, même direction.
                              </span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-red-200 mt-0.5">•</span>
                              <span>
                                <strong>Deux pistes techniques</strong>: résolution (troubleshoot) ET restauration (plan B) en parallèle.
                              </span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-red-200 mt-0.5">•</span>
                              <span>
                                <strong>Communication maîtrisée</strong>: interne + externe, cadence, messages alignés, pas de bruit.
                              </span>
                            </li>
                          </ul>
                        </div>

                        <div className="rounded-2xl border border-red-400/20 bg-red-500/10 p-5">
                          <h3 className="text-lg font-bold text-white mb-2">Déclencheurs typiques</h3>
                          <ul className="space-y-2 text-sm text-white/80">
                            <li className="flex items-start gap-2">
                              <span className="text-amber-200 mt-0.5">•</span>
                              <span>Risque de panne majeure / indisponibilité cloud</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-amber-200 mt-0.5">•</span>
                              <span>Incident sécurité avec impact opérationnel</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-amber-200 mt-0.5">•</span>
                              <span>Dégradation critique (latence, pertes, erreurs)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-amber-200 mt-0.5">•</span>
                              <span>Menace sur \(RTO/RPO\) / sauvegardes / restauration</span>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                        <h3 className="text-lg font-bold text-white mb-2">Idées “out of the box” (pour être meilleurs)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/80">
                          <div className="space-y-2">
                            <p className="font-semibold text-white">Exécution / war room</p>
                            <ul className="space-y-1.5">
                              <li>• “Incident kit” automatique: canal Teams, doc timeline, checklist, contacts, modèles messages.</li>
                              <li>• Cadence standard: update interne toutes les 15 min, client toutes les 30–60 min (selon sévérité).</li>
                              <li>• Decision log: qui décide quoi, quand, pourquoi (évite le chaos et facilite le postmortem).</li>
                              <li>• Rôles rotatifs: relève pour éviter fatigue + erreurs (surtout si nuit/24–48h).</li>
                            </ul>
                          </div>
                          <div className="space-y-2">
                            <p className="font-semibold text-white">Prévention / amélioration continue</p>
                            <ul className="space-y-1.5">
                              <li>• GameDays trimestriels (simulation panne/restauration) + scoring RTO/RPO réel.</li>
                              <li>• “SLO / Error budget” cloud: déclencheurs proactifs avant que ça brûle.</li>
                              <li>• Templates client par secteur (incl. BZ Telecom): messages prêts, ton, responsabilités.</li>
                              <li>• Postmortem blameless + actions SMART + owner + date + suivi au comité.</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-emerald-400/15 bg-emerald-500/10 p-5">
                        <p className="text-sm text-white/85">
                          <strong>BZ Telecom (téléphonie cloud):</strong> lors de certains CODE ROUGE, on “prend en charge” particulièrement la{' '}
                          <strong>communication</strong> (interne/externe) et on assiste aussi l’équipe CLOUD sur l’infra (haut niveau).
                        </p>
                      </div>

                      {/* Standards Internationaux Intégrés */}
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Shield className="w-5 h-5 text-blue-200" />
                          <h3 className="text-lg font-bold text-white">Alignement avec les Standards Internationaux</h3>
                        </div>
                        <p className="text-sm text-white/80 mb-4">
                          CODE ROUGE BZ est aligné avec les meilleures pratiques internationales pour garantir une gestion de crise professionnelle et efficace.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="rounded-xl border border-blue-400/20 bg-blue-500/10 p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <BookOpen className="w-4 h-4 text-blue-200" />
                              <p className="font-semibold text-white">ITIL 4</p>
                            </div>
                            <p className="text-xs text-white/80">
                              Gestion d'incidents majeurs, war room, communication structurée, escalade, résolution double piste (troubleshoot + restauration).
                            </p>
                          </div>
                          <div className="rounded-xl border border-purple-400/20 bg-purple-500/10 p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Workflow className="w-4 h-4 text-purple-200" />
                              <p className="font-semibold text-white">COBIT 2019</p>
                            </div>
                            <p className="text-xs text-white/80">
                              Gouvernance claire (RACI), approche holistique, traçabilité des décisions, gestion des risques, amélioration continue.
                            </p>
                          </div>
                          <div className="rounded-xl border border-green-400/20 bg-green-500/10 p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Shield className="w-4 h-4 text-green-200" />
                              <p className="font-semibold text-white">NIST CSF 2.0</p>
                            </div>
                            <p className="text-xs text-white/80">
                              Réponse aux incidents (RESPOND), coordination, communication parties prenantes, implémentation réponse, amélioration post-incident.
                            </p>
                          </div>
                          <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Award className="w-4 h-4 text-amber-200" />
                              <p className="font-semibold text-white">ISO 27001 & ISO 22301</p>
                            </div>
                            <p className="text-xs text-white/80">
                              SMSI (Sécurité), continuité d'activité, RTO/RPO, plan de continuité, tests et exercices, amélioration continue.
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'equipe' && (
                    <motion.div
                      key="equipe"
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      transition={{ duration: 0.22 }}
                      className="space-y-6"
                    >
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div>
                            <h3 className="text-lg font-bold text-white">Organigramme “War Room”</h3>
                            <p className="text-sm text-white/75">
                              Le <strong>chargé de projet</strong> est le pont: il synchronise la vérité, la cadence, et les décisions.
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Pill tone="red">Incident</Pill>
                            <Pill tone="emerald">Restauration</Pill>
                            <Pill tone="amber">Troubleshoot</Pill>
                          </div>
                        </div>

                        <div className="mt-6 relative">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
                            {/* Comms */}
                            <div className="rounded-2xl border border-sky-400/20 bg-sky-500/10 p-5 md:col-span-1">
                              <div className="flex items-center gap-2 mb-2">
                                <MessageSquareText className="w-5 h-5 text-sky-200" />
                                <h4 className="font-bold text-white">Communication (interne + externe)</h4>
                              </div>
                              <ul className="text-sm text-white/80 space-y-1.5">
                                <li>• Messages clairs, pas de spéculation</li>
                                <li>• Cadence d’updates + status page / courriels</li>
                                <li>• Alignement: une seule source de vérité</li>
                              </ul>
                              <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Radio className="w-4 h-4 text-white/80" />
                                  <p className="text-sm font-semibold text-white">BZ Telecom</p>
                                </div>
                                <p className="text-xs text-white/75">
                                  On les supporte souvent surtout côté <strong>communication</strong> et coordination client.
                                </p>
                              </div>
                            </div>

                            {/* PM */}
                            <div className="rounded-2xl border border-white/15 bg-white/8 p-5 md:col-span-1 relative overflow-hidden">
                              <div className="absolute inset-0 bg-linear-to-br from-white/10 via-transparent to-transparent" />
                              <div className="relative">
                                <div className="flex items-center gap-2 mb-2">
                                  <Users className="w-5 h-5 text-amber-200" />
                                  <h4 className="font-extrabold text-white">Chargé de projet (Pont)</h4>
                                </div>
                                <p className="text-sm text-white/80">
                                  Coordination globale: priorités, arbitrages, suivi, décisions, et alignement de toutes les cellules.
                                </p>
                                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                                    <p className="font-semibold text-white mb-0.5">Cadence</p>
                                    <p className="text-white/70">rituels + updates</p>
                                  </div>
                                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                                    <p className="font-semibold text-white mb-0.5">Décisions</p>
                                    <p className="text-white/70">log + arbitrage</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Tech */}
                            <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-5 md:col-span-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Wrench className="w-5 h-5 text-amber-200" />
                                <h4 className="font-bold text-white">Équipe technique (Troubleshoot)</h4>
                              </div>
                              <ul className="text-sm text-white/80 space-y-1.5">
                                <li>• Diagnostiquer / isoler / corriger</li>
                                <li>• Mitigations rapides (réduction impact)</li>
                                <li>• Proposer options + risques</li>
                              </ul>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="hidden md:block" />

                            <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-5">
                              <div className="flex items-center gap-2 mb-2">
                                <LifeBuoy className="w-5 h-5 text-emerald-200" />
                                <h4 className="font-bold text-white">Équipe technique (Restauration / Plan B)</h4>
                              </div>
                              <ul className="text-sm text-white/80 space-y-1.5">
                                <li>• Préparer rollback / restore / DR</li>
                                <li>• Valider sauvegardes + RTO/RPO</li>
                                <li>• Exécuter si la résolution échoue</li>
                              </ul>
                            </div>

                            <div className="hidden md:block" />
                          </div>

                          {/* Connectors (simple, desktop only) */}
                          <div className="hidden md:block pointer-events-none">
                            {/* Horizontal lines to center */}
                            <div className="absolute left-[33.5%] right-[33.5%] top-[30%] h-px bg-white/15" />
                            <div className="absolute left-[33.5%] right-[33.5%] top-[74%] h-px bg-white/10" />
                            {/* Vertical line down from center */}
                            <div className="absolute left-1/2 -translate-x-1/2 top-[38%] bottom-[26%] w-px bg-white/12" />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                        <h3 className="text-lg font-bold text-white mb-2">Rituels minimaux (suggestion)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-white/80">
                          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <p className="font-semibold text-white mb-1">T+0 — Activation</p>
                            <p>War room, rôles assignés, scope, première communication interne.</p>
                          </div>
                          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <p className="font-semibold text-white mb-1">T+15 — Stabilisation</p>
                            <p>Hypothèses, métriques, mitigation, plan B prêt.</p>
                          </div>
                          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <p className="font-semibold text-white mb-1">T+30/60 — Updates</p>
                            <p>Client: impact/ETA/next update. Interne: décisions + action owners.</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'roles' && (
                    <motion.div
                      key="roles"
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      transition={{ duration: 0.22 }}
                      className="space-y-6"
                    >
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                        <h3 className="text-lg font-bold text-white mb-4">Rôles & responsabilités (holistique)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="rounded-xl border border-white/10 bg-white/5 p-5">
                            <div className="flex items-center gap-2 mb-2">
                              <Users className="w-5 h-5 text-amber-200" />
                              <p className="font-bold text-white">Chargé de projet (pont)</p>
                            </div>
                            <ul className="text-sm text-white/80 space-y-1.5">
                              <li>• Cadence des points / synchronisation</li>
                              <li>• Decision log + arbitrages</li>
                              <li>• Gestion des priorités + escalades</li>
                              <li>• Pont: comms ↔ tech ↔ direction ↔ client</li>
                            </ul>
                          </div>

                          <div className="rounded-xl border border-sky-400/20 bg-sky-500/10 p-5">
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquareText className="w-5 h-5 text-sky-200" />
                              <p className="font-bold text-white">Communication</p>
                            </div>
                            <ul className="text-sm text-white/80 space-y-1.5">
                              <li>• Un message, une vérité (pas de contradictions)</li>
                              <li>• Updates internes + externes</li>
                              <li>• Ton: transparent, rassurant, factuel</li>
                              <li>• Prise en charge BZ Telecom si applicable</li>
                            </ul>
                          </div>

                          <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-5">
                            <div className="flex items-center gap-2 mb-2">
                              <Wrench className="w-5 h-5 text-amber-200" />
                              <p className="font-bold text-white">Tech — Troubleshoot</p>
                            </div>
                            <ul className="text-sm text-white/80 space-y-1.5">
                              <li>• Diagnostic + isolation</li>
                              <li>• Mitigation immédiate (réduire impact)</li>
                              <li>• Fix / rollback partiel (si sûr)</li>
                              <li>• Partager options + risques au pont</li>
                            </ul>
                          </div>

                          <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-5">
                            <div className="flex items-center gap-2 mb-2">
                              <LifeBuoy className="w-5 h-5 text-emerald-200" />
                              <p className="font-bold text-white">Tech — Restauration</p>
                            </div>
                            <ul className="text-sm text-white/80 space-y-1.5">
                              <li>• Vérifier backups + procédures restore</li>
                              <li>• Préparer environnement de bascule (DR)</li>
                              <li>• Exécuter plan B sans hésitation si requis</li>
                              <li>• Valider données / intégrité / retours service</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'raci' && (
                    <motion.div
                      key="raci"
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      transition={{ duration: 0.22 }}
                      className="space-y-6"
                    >
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-6 overflow-x-auto">
                        <h3 className="text-lg font-bold text-white mb-2">RACI holistique (v1)</h3>
                        <p className="text-sm text-white/70 mb-4">
                          L’objectif est de supprimer l’ambiguïté en crise. On ajuste les noms/owners exacts après tes réponses.
                        </p>

                        <table className="min-w-[980px] w-full text-sm">
                          <thead>
                            <tr className="text-left">
                              <th className="py-3 pr-4 text-white/80 font-semibold">Activités</th>
                              <th className="py-3 pr-3 text-white/80 font-semibold">Chargé de projet</th>
                              <th className="py-3 pr-3 text-white/80 font-semibold">Comms</th>
                              <th className="py-3 pr-3 text-white/80 font-semibold">Tech Troubleshoot</th>
                              <th className="py-3 pr-3 text-white/80 font-semibold">Tech Restauration</th>
                              <th className="py-3 pr-3 text-white/80 font-semibold">BZ Telecom</th>
                              <th className="py-3 pr-3 text-white/80 font-semibold">Client</th>
                            </tr>
                          </thead>
                          <tbody className="align-top">
                            {[
                              {
                                label: 'Activation CODE ROUGE + mise en place war room',
                                v: ['A', 'C', 'R', 'R', 'C', 'I'] as const,
                              },
                              {
                                label: 'Cadence + gouvernance + decision log',
                                v: ['A', 'C', 'C', 'C', 'C', 'I'] as const,
                              },
                              {
                                label: 'Communication interne (BZ) + alignement',
                                v: ['C', 'A', 'I', 'I', 'C', 'I'] as const,
                              },
                              {
                                label: 'Communication externe client (statut, impact, ETA)',
                                v: ['C', 'A', 'C', 'C', 'C', 'I'] as const,
                              },
                              {
                                label: 'Diagnostic / correction / mitigation',
                                v: ['C', 'I', 'A', 'C', 'I', 'I'] as const,
                              },
                              {
                                label: 'Préparer / exécuter restauration (Plan B / DR)',
                                v: ['C', 'I', 'C', 'A', 'I', 'I'] as const,
                              },
                              {
                                label: 'Prise en charge BZ Telecom (communication + coordination)',
                                v: ['C', 'A', 'I', 'I', 'R', 'I'] as const,
                              },
                              {
                                label: 'Clôture incident + “go/no-go” retour normal',
                                v: ['A', 'C', 'R', 'R', 'C', 'C'] as const,
                              },
                            ].map((row) => (
                              <tr key={row.label} className="border-t border-white/10">
                                <td className="py-3 pr-4 text-white/85">{row.label}</td>
                                {row.v.map((x, i) => (
                                  <td key={i} className="py-3 pr-3">
                                    <BadgeRA value={x} />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div className="mt-4 flex flex-wrap gap-2 text-xs text-white/70">
                          <Pill tone="red">A = Accountable</Pill>
                          <Pill tone="amber">R = Responsible</Pill>
                          <Pill tone="emerald">C = Consulted</Pill>
                          <Pill tone="slate">I = Informed</Pill>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'postmortem' && (
                    <motion.div
                      key="postmortem"
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      transition={{ duration: 0.22 }}
                      className="space-y-6"
                    >
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                        <h3 className="text-lg font-bold text-white mb-2">Postmortem (blameless) — obligatoire</h3>
                        <p className="text-sm text-white/80">
                          Après chaque incident, on fait un postmortem pour transformer la douleur en système. Pas de blâme, seulement des causes,
                          des apprentissages, et des actions.
                        </p>

                        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-white/80">
                          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <p className="font-semibold text-white mb-1">1) Timeline</p>
                            <p>Faits, horodatage, décisions, communications.</p>
                          </div>
                          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <p className="font-semibold text-white mb-1">2) Cause(s) racine</p>
                            <p>Technique + process + humain + fournisseur (si applicable).</p>
                          </div>
                          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <p className="font-semibold text-white mb-1">3) Actions</p>
                            <p>SMART: owner, deadline, validation, suivi comité.</p>
                          </div>
                        </div>

                        <div className="mt-5 rounded-xl border border-amber-400/20 bg-amber-500/10 p-4">
                          <p className="text-sm text-white/85">
                            <strong>Signal fort BZ:</strong> l’amélioration continue est un livrable. La crise se termine quand les actions sont
                            planifiées et suivies.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="p-4 md:p-5 border-t border-white/10 bg-black/40 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-white/60 flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4" />
                  <span>V1 “CODE ROUGE” — contenu ajustable selon vos standards internes</span>
                </div>
                <button
                  onClick={onClose}
                  className="inline-flex items-center gap-2 rounded-xl bg-white text-slate-950 px-5 py-2.5 font-semibold hover:bg-slate-100 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

